import React, {useContext, useEffect, useMemo, useReducer } from 'react';
import { styled } from '@mui/material/styles';
import BackgroundContext from '@/components/background/BackgroundContext';
import { ModalDialog } from '@/components';
import t from '@/utils/translate';
import IconButton from '@mui/material/IconButton';
import CrossIcon from '@/components/icons/Cross';
import { RootState, useAppSelector } from '@/state/store';
import { createSelector } from '@reduxjs/toolkit';
import { RequestStatus } from '@/contracts';
import { list as fetchProjects } from '@/loaders/project';
import CircularProgress from '@mui/material/CircularProgress';
import UploadIcon from '@/components/icons/Upload';
import DownloadIcon from '@/components/icons/Download';
import { Link } from 'react-router-dom';
import { Download, ProcessesList, QueuedUpload } from '@/components/background/Background.class';
import { ProjectData } from '@/state/types/project-data';
import { UploadStatus } from '@/state/types/model-upload';
import TableList, { ColumnMeta } from '@/components/TableList';
import { flexShrink } from '@mui/system';

const Window = styled('div')({
  padding: '16px',
  display: 'flex',
  gap: '16px',
  flexDirection: 'column',
});

const Header = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  gap: '10px',
  alignItems: 'center'
});

const Body = styled('div')({
  display: 'flex',
  gap: '10px',
  flexDirection: 'column',
  fontWeight: 400,
  fontSize: '13px',
});

const Title = styled('h1')({
  margin: '0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: '#091E42',
  fontWeight: 500,
  fontSize: '20px',
});

const CloseButton = styled(IconButton)({
  width: '28px',
  height: '28px',
  fontSize: '18px',
  padding: '5px',
  color: 'inherit',
  opacity: 0.2,
  ['&:hover']: {
    opacity: 1,
    backgroundColor: 'transparent',
  },
});

const ActionsBar = styled('div')({
  display: 'flex',
  gap: '10px',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
});

const ActionButton = styled('button')({
  height: '32px',
  padding: '9px 16px',
  fontWeight: 500,
  fontSize: '12px',
  borderRadius: '6px',
  border: '1px solid #DFE1E6',
  background: 'transparent',
  color: '#091E42',
  ['&:disabled']: {
    opacity: 0.5,
  },
});

const Warning = styled('p')({
  margin: 0,
  color: '#DE350B',
});

const Hint = styled('p')({
  margin: 0,
  padding: '10px 0',
  height: '35px',
  color: '#00875A',
});

const Name = styled(Link)({
  marginLeft: '10px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: '#344563',
  fontWeight: 500,
  fontSize: '14px',
  textDecoration: 'none',
});

const Type = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: '#6B778C',
  '&>*': {
    flexShrink: 0
  }
});

const Amount = styled('div')({
  paddingLeft: '10px',
  color: '#6B778C',
  '& strong': {
    fontWeight: 400,
    color: '#091E42'
  }
});

type Process = {
  pid: string,
  type: 'U' | 'D',
  amount: number,
  loaded: number,
  total: number,
  totals: Record<string, number>;
  bytes: Record<string, number>;
};

type ProcessList = Record<string, Process>;

const getProgress = (loaded: number, total?: number) => Math.min(total ? Math.round(loaded * 100 / total) : 0, 100);

type Props = {
  onClose: () => void
};

function actualProcess(type: 'U', pid: string, from: ProcessList, to: ProcessList, list: QueuedUpload[]): void;
function actualProcess(type: 'D', pid: string,  from: ProcessList, to: ProcessList, list: Download[]): void;
function actualProcess(type: 'U' | 'D', pid: string,  from: ProcessList, to: ProcessList, list: unknown[]): void {
  if (!list.length) {
    if (type === 'U') console.log('yoba');
    return;
  }
  const p: Process = from[`${type}:${pid}`] || {
    pid,
    type,
    amount: 0,
    loaded: 0,
    total: 0,
    totals: {},
    bytes: {},
  };
  p.amount = 0;

  switch (type) {
    case 'U':
      (list as QueuedUpload[]).forEach((u) => {
        if (u.status == UploadStatus.SCHEDULED || u.status === UploadStatus.INPROGRESS) {
          p.amount++;
          p.totals[u.name] = p.totals[u.name] || u.total;
          p.bytes[u.name] = u.loaded || 0;
        } else if (u.status === UploadStatus.SUCCESS) {
          if (p.totals[u.name]) {
            p.bytes[u.name] = p.totals[u.name];
          }
        } else if (u.status === UploadStatus.ABORTED || u.status === UploadStatus.FAILED) {
          delete p.totals[u.name];
          delete p.bytes[u.name];
        }
      });
      break;
    case 'D':
      (list as Download[]).forEach((d) => {
        p.amount++;
        p.totals[d.id] = p.totals[d.id] || d.total || 0;
        p.bytes[d.id] = d.loaded || 0;
      });
      break;
  }

  if (p.amount) {
    p.total = Object.values(p.totals).reduce((s, v) => s + v, 0);
    p.loaded = Object.values(p.bytes).reduce((s, v) => s + v, 0);
    to[`${type}:${pid}`] = p;
  }
}

function actualize(from: ProcessList, to: ProcessesList): ProcessList {
  const result: ProcessList = {};
  to.forEach(({ uploads, downloads }, pid) => {
    actualProcess('U', pid, from, result, uploads);
    actualProcess('D', pid, from, result, downloads);
  });
  return result;
}

const ColumnMetaStyles = {
  headerStyle: { height: '34px' },
  cellStyle: { height: '40px'},
};

export default ({ onClose }: Props) => {
  const background = useContext(BackgroundContext);

  const { projects, status } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.list,
        (state: RootState) => state.projects.listFetchStatus,
      ],
      (projects, status) => ({ projects, status })
    )
  );

  const [processes, setProcesses] = useReducer(
    (state: ProcessList, actual: ProcessesList) => {
      return actualize(state, actual);
    },
    actualize({}, background.list),
  );

  const projectsMap = useMemo<Map<string, ProjectData>>(() => (projects || []).reduce((m, p) => {
    m.set(p.code, p);
    return m;
  }, new Map()), [projects]);

  const list = useMemo(() => Object.values(processes).sort((a, b) => {
    const an = projectsMap.get(a.pid)?.name || '';
    const bn = projectsMap.get(b.pid)?.name || '';
    return an.localeCompare(bn);
  }), [processes, projectsMap]);

  
  const columns = useMemo<ColumnMeta[]>(() => ([
    {
      attrName: 'project',
      title: t('background.column.project'),
      noSort: true,
      ...ColumnMetaStyles,
      dataGetter: ({ pid }: Process) => (
        <Name to={`/project/${pid}`}>{projectsMap.get(pid)?.name || pid}</Name>
      ),
    },
    {
      attrName: 'process',
      title: t('background.column.process'),
      width: 110,
      noSort: true,
      ...ColumnMetaStyles,
      dataGetter: ({ type }: Process) => (
        <Type>
          {type === 'U'
            ? <><UploadIcon sx={{ fontSize: '16px' }}/>{t('background.captions.uploading')}</>
            : <><DownloadIcon size={16}/>{t('background.captions.downloading')}</>
          }
        </Type>
      ),
    },
    {
      attrName: 'progress',
      title: t('background.column.progress'),
      width: 90,
      noSort: true,
      ...ColumnMetaStyles,
      dataGetter: ({ amount }: Process) => (
        <Amount>
          <strong>{amount}</strong>
          &nbsp;{t('background.captions.files', { count: amount })}
        </Amount>
      ),
    },
    {
      width: 40,
      attrName: 'status',
      noSort: true,
      ...ColumnMetaStyles,
      dataGetter: ({ loaded, total }: Process) => {
        const progress = getProgress(loaded, total);
        return <CircularProgress
          variant={typeof progress === 'number' ? 'determinate' : 'indeterminate'}
          value={progress}
          size={18}
          sx={{ flexShrink: 0 }}
        />
      },
      type: 'iconic',
    }
  ]), [ projectsMap ]);
  
  useEffect(() => {
    if (status === RequestStatus.IDLE && !Array.isArray(projects)) {
      fetchProjects();
    }
  }, [status, projects]);

  useEffect(() => {
    const onProgress = () => {
      setProcesses(background.list);
    };
    background.addEventListener('uploadProgress', onProgress);
    background.addEventListener('downloadProgress', onProgress);
    return () => {
      background.removeEventListener('uploadProgress', onProgress);
      background.removeEventListener('downloadProgress', onProgress);
    };
  }, []);

  return <ModalDialog
    open={true}
    withoutHeader
    width={540}
    maxHeight="calc(100% - 120px)"
    onClose={onClose}
    PaperProps={{
      sx: {
        borderRadius: '10px !important',
      }
    }}
  >
    <Window>
      <Header>
        <Title>{t('background.title')}</Title>
        <CloseButton onClick={onClose}>
          <CrossIcon/>
        </CloseButton>
      </Header>
      <Body>
        { !list.length
          ? <Hint>{t('background.noActiveProcesses')}</Hint>
          : (<>
            <Warning>{t('background.interruptWarning')}</Warning>
            <TableList
              columns={columns}
              data={list}
            />
          </>)
        }
      </Body>
      <ActionsBar>
        <ActionButton onClick={onClose}>
          {t('background.close')}
        </ActionButton>
      </ActionsBar>
    </Window>
  </ModalDialog>;
};
